import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  CloudSun, Sun, CloudRain, Cloud, CloudSnow, CloudLightning, CloudDrizzle, Snowflake,
  DollarSign, TrendingUp, TrendingDown, Trophy, Minus,
  Newspaper, Plane, UtensilsCrossed, MonitorPlay, HeartPulse, Sparkles,
  ArrowRight, Calendar, Clock, CircleDollarSign, RefreshCw, MapPin, Search,
  MessageCircle
} from 'lucide-react';
import TopNavBar from '../components/TopNavBar';
import FooterDirectory from '../components/FooterDirectory';
import NewsTicker from '../components/NewsTicker';
import TVDramaWidget from '../components/TVDramaWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ---------- Weather icon helper ---------- */
const getWeatherIcon = (code, size = 'w-8 h-8') => {
  if (code === 0 || code === 1) return <Sun className={`${size} text-amber-400`} />;
  if (code === 2) return <CloudSun className={`${size} text-amber-300`} />;
  if (code === 3) return <Cloud className={`${size} text-gray-400`} />;
  if (code >= 45 && code <= 48) return <Cloud className={`${size} text-gray-500`} />;
  if (code >= 51 && code <= 55) return <CloudDrizzle className={`${size} text-blue-400`} />;
  if (code >= 61 && code <= 65) return <CloudRain className={`${size} text-blue-500`} />;
  if (code >= 71 && code <= 75) return <Snowflake className={`${size} text-sky-300`} />;
  if (code >= 80 && code <= 82) return <CloudRain className={`${size} text-blue-600`} />;
  if (code >= 95) return <CloudLightning className={`${size} text-yellow-500`} />;
  return <CloudSun className={`${size} text-amber-300`} />;
};

/* ============================================================
   WEATHER WIDGET (col-span-2)
   ============================================================ */
const WeatherCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/weather/forecast?city=Ciudad%20de%20M%C3%A9xico&days=3`)
      .then(r => r.json()).then(setData).catch(() => {});
  }, []);

  if (!data) return (
    <div data-testid="weather-widget" className="col-span-1 bg-white rounded-3xl border border-[#E0DCD0] p-4 md:p-5 shadow-[0_8px_30px_rgba(28,58,39,0.04)] animate-pulse min-h-[120px]" />
  );

  return (
    <a href="https://www.weather.com/es-MX/tiempo/hoy/l/Mexico+City+Mexico" target="_blank" rel="noopener noreferrer" data-testid="weather-widget" className="col-span-1 bg-white rounded-3xl border border-[#E0DCD0] p-4 md:p-5 shadow-[0_8px_30px_rgba(28,58,39,0.04)] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 block cursor-pointer">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#596A5F] font-['Figtree']">Clima ahora</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <MapPin className="w-3 h-3 text-[#D74235]" />
            <span className="text-xs font-medium text-[#1C3A27]/70 font-['Figtree']">{data.city}</span>
          </div>
        </div>
        {getWeatherIcon(data.code, 'w-9 h-9')}
      </div>
      <div className="flex items-end gap-2 mb-1">
        <span className="text-4xl lg:text-5xl font-semibold text-[#1C3A27] tracking-tight font-['Outfit'] leading-none">{data.temp}°</span>
        <span className="text-sm text-[#1C3A27]/60 font-['Figtree'] mb-1">{data.condition}</span>
      </div>
      {data.forecast?.length > 0 && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-[#E0DCD0]">
          {data.forecast.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 p-1.5 rounded-lg bg-[#F9F7F1]">
              <span className="text-[10px] font-bold text-[#596A5F] font-['Figtree']">{d.day}</span>
              {getWeatherIcon(d.code, 'w-5 h-5')}
              <div className="flex gap-1 text-[10px] font-['Figtree']">
                <span className="font-semibold text-[#1C3A27]">{d.high}</span>
                <span className="text-[#596A5F]">{d.low}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </a>
  );
};

/* ============================================================
   EXCHANGE RATE WIDGET (col-span-1)
   ============================================================ */
const ExchangeCard = () => {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchRates = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/exchange-rates`)
      .then(r => r.json()).then(d => { setRates(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  useEffect(fetchRates, [fetchRates]);

  return (
    <a href="https://www.eleconomista.com.mx/mercados/" target="_blank" rel="noopener noreferrer" data-testid="exchange-rate-widget" className="col-span-1 bg-white rounded-3xl border border-[#E0DCD0] p-4 md:p-5 shadow-[0_8px_30px_rgba(28,58,39,0.04)] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 flex flex-col justify-between cursor-pointer">
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#596A5F] font-['Figtree']">Tipo de cambio</p>
          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); fetchRates(); }} disabled={loading} className="p-1 rounded-full hover:bg-[#F0EDE1] transition-colors">
            <RefreshCw className={`w-3 h-3 text-[#596A5F] ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-1.5 mb-0.5">
          <CircleDollarSign className="w-4 h-4 text-[#D74235]" />
          <span className="text-[10px] font-semibold text-[#596A5F] font-['Figtree']">USD / MXN</span>
        </div>
        <div className="flex items-end gap-2">
          <span className="text-3xl font-semibold text-[#1C3A27] tracking-tight font-['Outfit'] leading-none">
            ${rates?.usd || '—'}
          </span>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-[#E0DCD0] flex items-center justify-between">
        <div>
          <span className="text-[10px] text-[#596A5F] font-['Figtree']">EUR / MXN</span>
          <p className="text-lg font-semibold text-[#1C3A27] font-['Outfit']">${rates?.eur || '—'}</p>
        </div>
        <TrendingUp className="w-4 h-4 text-emerald-500" />
      </div>
    </a>
  );
};

/* ============================================================
   LIGA MX TABLE WIDGET (col-span-2)
   ============================================================ */
const LigaMXCard = () => {
  const standings = [
    { pos: 1, team: 'Pumas UNAM', pts: 36, pj: 17, trend: 'up' },
    { pos: 2, team: 'Chivas', pts: 36, pj: 17, trend: 'up' },
    { pos: 3, team: 'Cruz Azul', pts: 33, pj: 17, trend: 'same' },
    { pos: 4, team: 'Pachuca', pts: 31, pj: 17, trend: 'up' },
  ];
  const lastClassic = { home: 'Chivas', homeScore: 1, away: 'América', awayScore: 0, date: 'Feb 2026' };

  return (
    <a href="https://www.ligamx.net" target="_blank" rel="noopener noreferrer" data-testid="liga-mx-widget" className="col-span-1 bg-white rounded-3xl border border-[#E0DCD0] p-4 md:p-5 shadow-[0_8px_30px_rgba(28,58,39,0.04)] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 block cursor-pointer">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#D74235]" />
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#596A5F] font-['Figtree']">Liga MX</p>
        </div>
        <span className="text-[9px] bg-[#1C3A27] text-white px-2 py-0.5 rounded-full font-bold tracking-wider font-['Figtree']">CLAUSURA 2026</span>
      </div>

      <div className="rounded-lg overflow-hidden border border-[#E0DCD0]">
        <div className="bg-[#1C3A27] text-white text-[9px] font-bold tracking-wider uppercase px-3 py-1.5 flex justify-between font-['Figtree']">
          <span>Equipo</span>
          <span>PJ &nbsp; PTS</span>
        </div>
        {standings.map((t, i) => (
          <div key={i} className={`flex items-center justify-between px-3 py-1.5 border-b border-[#E0DCD0] last:border-b-0 ${i < 4 ? 'bg-[#E8F0E4]/40' : ''} hover:bg-[#E8F0E4]/60 transition-colors`}>
            <div className="flex items-center gap-2">
              <span className="w-4 text-xs font-bold text-[#596A5F] font-['Outfit']">{t.pos}</span>
              <span className="text-xs font-medium text-[#1C3A27] font-['Figtree']">{t.team}</span>
              {t.trend === 'up' && <TrendingUp className="w-3 h-3 text-emerald-500" />}
              {t.trend === 'down' && <TrendingDown className="w-3 h-3 text-[#D74235]" />}
              {t.trend === 'same' && <Minus className="w-3 h-3 text-gray-400" />}
            </div>
            <div className="flex gap-3 text-xs font-['Outfit']">
              <span className="text-[#596A5F] w-5 text-center">{t.pj}</span>
              <span className="font-bold text-[#1C3A27] w-5 text-center">{t.pts}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-2.5 p-2.5 bg-[#F9F7F1] rounded-lg">
        <p className="text-[9px] uppercase tracking-wider font-bold text-[#596A5F] mb-1 font-['Figtree']">Último Clásico</p>
        <div className="flex items-center justify-center gap-3">
          <span className="text-xs font-semibold text-[#1C3A27] font-['Figtree']">{lastClassic.home}</span>
          <div className="flex items-center gap-1 bg-[#1C3A27] text-white px-2.5 py-0.5 rounded-md">
            <span className="text-sm font-bold font-['Outfit']">{lastClassic.homeScore}</span>
            <span className="text-[10px] text-white/50">-</span>
            <span className="text-sm font-bold font-['Outfit']">{lastClassic.awayScore}</span>
          </div>
          <span className="text-xs font-semibold text-[#1C3A27] font-['Figtree']">{lastClassic.away}</span>
        </div>
      </div>
    </a>
  );
};

/* ============================================================
   PAZ ASSISTANT CARD (col-span-1)
   ============================================================ */
const PazCard = () => {
  const suggestions = [
    '¿Noticias de hoy?',
    '¿Clima en CDMX?',
    '¿Liga MX resultados?',
  ];

  const handleClick = () => {
    const pazBtn = document.querySelector('[data-testid="paz-open-button"]') || document.querySelector('.paz-helper-button');
    if (pazBtn) pazBtn.click();
  };

  return (
    <div data-testid="paz-assistant-card" className="col-span-1 bg-gradient-to-br from-[#F9F7F1] to-[#E8F0E4] rounded-3xl border border-[#E0DCD0] p-4 md:p-5 shadow-[0_8px_30px_rgba(28,58,39,0.04)] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-xl bg-[#D74235] flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[#1C3A27] font-['Outfit']">Pregúntale a Paz</h3>
          <p className="text-[10px] text-[#596A5F] font-['Figtree']">Tu asistente virtual</p>
        </div>
      </div>
      <p className="text-xs text-[#1C3A27]/70 font-['Figtree'] mb-2 leading-relaxed">
        Noticias, clima, deportes y más. Gratis.
      </p>
      <div className="flex flex-col gap-1.5 flex-1">
        {suggestions.map((q, i) => (
          <button
            key={i}
            onClick={handleClick}
            data-testid={`paz-suggestion-${i}`}
            className="text-left text-xs px-2.5 py-1.5 rounded-lg border border-[#D74235]/20 text-[#D74235] font-medium font-['Figtree'] hover:bg-[#D74235]/10 transition-colors duration-200"
          >
            {q}
          </button>
        ))}
      </div>
      <button
        onClick={handleClick}
        data-testid="paz-open-chat-btn"
        className="mt-2.5 w-full py-2 bg-[#D74235] text-white rounded-lg font-semibold text-xs font-['Figtree'] hover:bg-[#c13a2f] transition-colors flex items-center justify-center gap-1.5"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        Hablar con Paz
      </button>
    </div>
  );
};

/* ============================================================
   CATEGORY NAVIGATION GRID
   ============================================================ */
const categories = [
  { name: 'Noticias', icon: Newspaper, href: 'https://www.jornada.com.mx', external: true, color: '#D74235' },
  { name: 'Clima', icon: CloudSun, href: 'https://www.weather.com/es-MX/tiempo/hoy/l/Mexico+City', external: true, color: '#E4A010' },
  { name: 'Liga MX', icon: Trophy, href: 'https://www.ligamx.net', external: true, color: '#1C7C2D' },
  { name: 'Finanzas', icon: CircleDollarSign, href: 'https://www.eleconomista.com.mx', external: true, color: '#2563EB' },
  { name: 'Viajes', icon: Plane, href: 'https://www.mexicodesconocido.com.mx', external: true, color: '#0891B2' },
  { name: 'Gastronomía', icon: UtensilsCrossed, href: 'https://www.cocinafacil.com.mx', external: true, color: '#EA580C' },
  { name: 'Entretenimiento', icon: MonitorPlay, href: 'https://www.televisa.com/entretenimiento', external: true, color: '#9333EA' },
  { name: 'Salud', icon: HeartPulse, href: 'https://www.gob.mx/salud', external: true, color: '#E11D48' },
];

const CategoryGrid = () => (
  <div data-testid="category-grid" className="flex flex-wrap gap-2">
    {categories.map((cat) => {
      const Icon = cat.icon;
      return (
        <a
          key={cat.name}
          href={cat.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`category-${cat.name.toLowerCase()}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E0DCD0] hover:bg-[#F0EDE1] hover:-translate-y-0.5 transition-all duration-200 group"
        >
          <Icon className="w-3.5 h-3.5" style={{ color: cat.color }} />
          <span className="text-xs font-semibold text-[#1C3A27] font-['Figtree'] group-hover:text-[#D74235] transition-colors">{cat.name}</span>
        </a>
      );
    })}
  </div>
);

/* ============================================================
   BLOG PREVIEW CARDS
   ============================================================ */
const blogImages = [
  'https://images.unsplash.com/photo-1592444455562-39f11e736929?w=400&h=260&fit=crop&q=75&fm=webp',
  'https://images.unsplash.com/photo-1707603571504-86c1ea50903e?w=400&h=260&fit=crop&q=75&fm=webp',
  'https://images.unsplash.com/photo-1713878620016-35be7a276a59?w=400&h=260&fit=crop&q=75&fm=webp',
];

const BlogPreview = () => {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/api/blog`)
      .then(r => r.json())
      .then(d => {
        const arts = (d.articles || []).slice(0, 3);
        setArticles(arts);
      })
      .catch(() => {});
  }, []);

  if (articles.length === 0) return null;

  return (
    <div data-testid="blog-preview" className="mt-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl sm:text-2xl font-semibold text-[#1C3A27] tracking-tight font-['Outfit']">Lo último del blog</h2>
        <Link to="/blog" className="text-sm font-semibold text-[#D74235] hover:underline flex items-center gap-1 font-['Figtree']">
          Ver todo <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {articles.map((a, i) => {
          const excerpt = a.content ? a.content.replace(/<[^>]+>/g, '').substring(0, 100) + '...' : '';
          const img = a.image || blogImages[i % blogImages.length];
          return (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              data-testid={`blog-card-${i}`}
              className="group bg-white rounded-2xl border border-[#E0DCD0] overflow-hidden shadow-[0_8px_30px_rgba(28,58,39,0.04)] hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              <div className="relative h-36 overflow-hidden">
                <img src={img} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                {a.category && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#D74235] text-white text-[9px] font-bold uppercase tracking-wider rounded-full font-['Figtree']">{a.category}</span>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-[#1C3A27] font-['Outfit'] leading-snug line-clamp-2 group-hover:text-[#D74235] transition-colors">{a.title}</h3>
                <p className="text-xs text-[#596A5F] font-['Figtree'] mt-1.5 line-clamp-2">{excerpt}</p>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-[#596A5F] font-['Figtree']">
                  <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{a.date}</span>
                  {a.read_time && <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{a.read_time}</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* NewsTicker imported from ../components/NewsTicker */

/* ============================================================
   HOME PAGE
   ============================================================ */
const HomePage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F1' }}>
      <Helmet>
        <title>Portal México - Tu Página de Inicio | Noticias, Clima, Liga MX y Paz</title>
        <meta name="description" content="Portal México: noticias México hoy, clima CDMX, Liga MX resultados, tipo de cambio dólar y recetas. Pregúntale a Paz, tu asistente virtual gratis." />
      </Helmet>

      <TopNavBar />
      {/* Spacer for fixed navbar (two rows: h-14 top + h-10 menu = ~96px) */}
      <div className="h-14 lg:h-24" />
      <NewsTicker />

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-3 md:py-5">
        {/* Hero heading */}
        <div className="mb-3">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C3A27] tracking-tight font-['Outfit'] leading-tight">
            Buenos días, México
          </h1>
          <p className="text-sm text-[#596A5F] font-['Figtree'] mt-0.5">
            Tu portal diario con las noticias, el clima, los deportes y todo lo que necesitas.
          </p>
        </div>

        {/* Category Navigation */}
        <CategoryGrid />

        {/* Bento Grid: Weather + Exchange + Liga MX + Paz in single row, then Telenovelas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <WeatherCard />
          <ExchangeCard />
          <LigaMXCard />
          <PazCard />
          <TVDramaWidget />
        </div>

        {/* Blog Preview */}
        <BlogPreview />
      </main>

      <FooterDirectory />
    </div>
  );
};

export default HomePage;
